import {
	Component, Input, OnDestroy, OnChanges, AfterViewInit, ViewChild, SimpleChanges, OnInit, ChangeDetectorRef
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NavigationService } from 'netview';
import { MessageService } from 'base';
import { IdentityService } from 'identity';
import { PatchManagerData, ToolsService } from 'tools';
import { DiscResultsService } from 'discovery';
import { Room, Building, Campus, ExtraIfInfo, JackToSwitchConnection, PatchManagerResultDto, SwitchToJackConnection } from 'UvExplorerServer.Dtos.TS/Tools';
import { Subscription } from 'rxjs';


class RoomJackToSwitchEntry {
	public dataKey: string;
	public jackRoom: string;
	public jackLabel: string;
	public switchName: string;
	public switchIpAddress: string;
	public ifAlias: string;
	public ifName: string;
	public ifIndex: string;
	public ifVLANs: string;
	public connectedDevices: string;

	constructor() {
		this.dataKey = "";

		this.jackRoom = "";
		this.jackLabel = "";

		this.switchName = "";
		this.switchIpAddress = "";
		this.ifAlias = "";
		this.ifName = "";
		this.ifIndex = "";
		this.ifVLANs = "";

		this.connectedDevices = "";
	}
}


@Component({
	selector: 'room-jacks-lookup',
	templateUrl: './room-jacks-lookup.component.html',
	styleUrls: ['./room-jacks-lookup.component.scss']
})
export class RoomJacksLookupComponent implements OnChanges, OnDestroy, OnInit {

	@Input()
	public ifTableHeightVh: number;

	@Input()
	public ifTableHeightPx: number;

	@Input()
	public ifTableMinHeight: number;

	@Input()
	public visible: boolean;

	public form: UntypedFormGroup;

	public campusName: UntypedFormControl;
	public buildingName: UntypedFormControl;
	public roomName: UntypedFormControl;

	private allCampuses: Campus[];
	private allBuildings: Building[];
	private allRooms: Room[];

	public campusNames: string[];
	public buildingNames: string[];
	public roomNames: string[];

	public cols: any[];
	public entries: RoomJackToSwitchEntry[];

	public first: number;

	private networkLoadedSub: Subscription;
	private patchManagerDataLoadedSub: Subscription;

	constructor(
		private navService: NavigationService,
		private messageService: MessageService,
		private discResultsService: DiscResultsService,
		private fb: UntypedFormBuilder,
		private toolsService: ToolsService,
		private identityService: IdentityService,
		private changeDetector: ChangeDetectorRef
	) {
		this.visible = true;

		this.campusName = new UntypedFormControl("", Validators.required);
		this.buildingName = new UntypedFormControl("", Validators.required);
		this.roomName = new UntypedFormControl("", Validators.required);

		this.form = this.fb.group({
			campusName: this.campusName,
			buildingName: this.buildingName,
			roomName: this.roomName,
		});

		this.entries = [];
		this.cols = [
			{ field: 'jackRoom', header: 'Jack Room', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'jackLabel', header: 'Jack Label', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'switchName', header: 'Switch', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'switchIpAddress', header: 'IP Address', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ifAlias', header: 'Alias', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ifName', header: 'Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ifVLANs', header: 'VLAN(s)', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'connectedDevices', header: 'Connected Device(s)', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
		];
		this.first = 0;

		this.networkLoadedSub = null;
		this.patchManagerDataLoadedSub = null;
	}

	public ngOnChanges(changes: SimpleChanges): void {
	}

	public ngOnInit(): void {
		if (this.navService.isNetworkLoaded) {
			this.initData();
		}
		else {
			this.networkLoadedSub = this.discResultsService.onNetworkLoaded.subscribe(
				() => {
					this.networkLoadedSub.unsubscribe();
					this.networkLoadedSub = null;

					this.initData();
				},
				(error) => {
					this.messageService.displayErrorMessage("Could not retrieve Patch Manager data");
				});
		}
	}

	public ngOnDestroy(): void {
		if (this.networkLoadedSub) {
			this.networkLoadedSub.unsubscribe();
			this.networkLoadedSub = null;
		}
		if (this.patchManagerDataLoadedSub) {
			this.patchManagerDataLoadedSub.unsubscribe();
			this.patchManagerDataLoadedSub = null;
		}
	}

	private initData(): void {
		if (this.toolsService.hasPatchManagerData) {

			if (this.toolsService.patchManagerData) {
				this.allCampuses = [...this.toolsService.patchManagerData.campuses];
				this.allBuildings = [...this.toolsService.patchManagerData.buildings];
			}
			this.onGotData();
		}
		else {
			this.patchManagerDataLoadedSub = this.toolsService.patchManagerDataLoaded.subscribe(
				(patchManagerData: PatchManagerData) => {
					this.patchManagerDataLoadedSub.unsubscribe();
					this.patchManagerDataLoadedSub = null;

					this.allCampuses = [...patchManagerData.campuses];
					this.allBuildings = [...patchManagerData.buildings];

					this.onGotData();
				});

			this.toolsService.loadPatchManagerData();
		}
				
	}

	private onGotData(): void {

		this.campusNames = [];
		this.buildingNames = [];

		if (!!this.allCampuses && !!this.allBuildings) {

			this.allCampuses.forEach((campus: Campus) => {
				this.campusNames.push(campus.name);
			});

			let firstCampusName: string = ((this.campusNames.length > 0) ? this.campusNames[0] : "");
			this.campusName.setValue(firstCampusName);
			console.log("First Campus Name: " + firstCampusName);

			this.loadBuildingNames(firstCampusName);
		}
	}

	private loadBuildingNames(campusName: string): void {

		this.buildingNames = [];

		if (campusName) {
			let campusPrefix: string = campusName + ",";
			this.allBuildings.forEach((building: Building) => {
				if (building.name.startsWith(campusPrefix)) {
					let buildingName: string = building.name.substring(campusPrefix.length).trim();
					this.buildingNames.push(buildingName);
				}
			});
		}

		let firstBuildingName = ((this.buildingNames.length > 0) ? this.buildingNames[0] : "");
		this.buildingName.setValue(firstBuildingName);
		console.log("First Building Name: " + firstBuildingName);

		this.loadRoomNames(campusName, firstBuildingName);
	}

	private loadRoomNames(campusName: string, buildingName: string): void {

		this.roomNames = [];

		if (campusName && buildingName) {
			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			this.messageService.startWork();

			this.toolsService.getBuildingRooms(sessionGuid, netViewGuid, agentGuid, schedDiscGuid, schedDiscRsltGuid, campusName, buildingName).subscribe(
				(result: Room[]) => {
					this.messageService.endWork();
					this.allRooms = result;

					if (this.allRooms) {
						this.allRooms.forEach((room: Room) => {
							this.roomNames.push(room.label);
						});

						let firstRoomName = ((this.roomNames.length > 0) ? this.roomNames[0] : "");
						this.roomName.setValue(firstRoomName);
						console.log("First Room Name: " + firstRoomName);

						this.changeDetector.detectChanges();
					}
				},
				(error) => {
					this.messageService.endWork();
					this.onGotData();
					this.messageService.displayErrorMessage("Rooms download failed");
				});
		}
	}

	public onCampusChanged(): void {
		this.loadBuildingNames(this.campusName.value);
		this.roomName.setValue("");
	}

	public onBuildingChanged(): void {
		this.loadRoomNames(this.campusName.value, this.buildingName.value);
	}

	public get enableLookup(): boolean {
		return ((!!this.campusName.value) && (!!this.buildingName.value) && (!!this.roomName.value))
	}

	public lookup(): void {

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
		let doLive = false;

		this.messageService.startWork();
		this.toolsService.lookupRoomToJacks(sessionGuid, netViewGuid, agentGuid, schedDiscGuid, schedDiscRsltGuid,
			this.campusName.value, this.buildingName.value, this.roomName.value, doLive).subscribe(
				(result: PatchManagerResultDto) => {
					this.messageService.endWork();
					this.rebuildEntries(result);
				},
				(error) => {
					this.messageService.endWork();
					this.rebuildEntries(null);
					this.messageService.displayErrorMessage("Could not retrieve jack information for this campus/building/room");
				});
	}


	public get ifTableHeight(): string {
		let result = `max(calc(${this.ifTableHeightVh}vh + ${this.ifTableHeightPx}px), ${this.ifTableMinHeight}px)`;
		return result;
	}

	public get enableSelectDevice(): boolean {
		return true;
	}

	public refresh(): void {
		this.lookup();
	}

	private rebuildEntries(result: PatchManagerResultDto): void {

		this.entries = [];

		if (!!result && !!result.roomJacksConnections) {

			result.roomJacksConnections.forEach((jack: JackToSwitchConnection) => {
				let tableEntry = new RoomJackToSwitchEntry();
				tableEntry.jackRoom = jack.jack_room;
				tableEntry.jackLabel = jack.jack_label;
				tableEntry.switchName = jack.switch_name;
				tableEntry.switchIpAddress = jack.switch_ip;
				tableEntry.ifAlias = jack.ifAlias;
				tableEntry.ifName = jack.ifName;
				tableEntry.ifIndex = jack.ifIndex;
				tableEntry.ifVLANs = jack.ifVLANs;
				tableEntry.connectedDevices = jack.connectedDevices;

				this.entries.push(tableEntry);
			});
			this.changeDetector.detectChanges();
		}
	}

	public onFilterChange(): void {

	}

	public doCustomSort(event): void {
		let comparer = function (a: RoomJackToSwitchEntry, b: RoomJackToSwitchEntry): number {
			let result = 0;

			// Compare string fields
			result = a[event.field].localeCompare(b[event.field]);
			return result * event.order;
		}

		this.entries.sort(comparer);
	}

}
